import dayjs from 'dayjs';
import { useMemo } from 'react';

import {
  useFrameHistoryListQuery,
  useUpdateFrameWorkStateMutation,
} from '@/hook';
import { RestartAlt, VIconButton } from '@/package';
import { useTaskFrameStore } from '@/store';

const REVERT_TIME_LIMIT_SECOND = 600;

export const TaskLayoutHeaderWorkStateRevertButton = () => {
  const { currentFrame, prevWorkState } = useTaskFrameStore(state => ({
    currentFrame: state.currentFrame!,
    prevWorkState: state.getPrevWorkState(),
  }));

  const currentWorkState = currentFrame?.workState;
  const frameSeq = currentFrame?.frameSeq;
  const taskID = currentFrame?.taskID;

  const hasRevertPermissionByRole = useMemo(
    () => useTaskFrameStore.getState().hasRevertPermissionByRole,
    [],
  );
  const isRevertDisabledByWorkState = useMemo(
    () => useTaskFrameStore.getState().isRevertDisabledByWorkState,
    [],
  );
  const isWorkingRevertConditionSatisfied = useMemo(
    () => useTaskFrameStore.getState().isWorkingRevertConditionSatisfied,
    [],
  );

  const isHistoryListQueryEnabled =
    !isRevertDisabledByWorkState() && hasRevertPermissionByRole();

  const { data: historyList } = useFrameHistoryListQuery(
    {
      frameSeq,
      groupWorkState: true,
      taskID,
    },
    isHistoryListQueryEnabled,
  );

  const { mutateAsync: updateFrameWorkState } =
    useUpdateFrameWorkStateMutation();

  const prevHistory = historyList?.find(
    history => history.workState === prevWorkState,
  );
  const prevHistoryCreatedAt = prevHistory?.createdAt;

  const isRevertExpired = !prevHistoryCreatedAt
    ? true
    : dayjs().diff(prevHistoryCreatedAt, 's') > REVERT_TIME_LIMIT_SECOND;

  const isRevertButtonVisible =
    !isRevertDisabledByWorkState() &&
    hasRevertPermissionByRole() &&
    isWorkingRevertConditionSatisfied() &&
    !isRevertExpired &&
    currentWorkState != null;

  const handleClick = () => {
    if (!prevWorkState || currentWorkState === prevWorkState) return;
    if (frameSeq == null || taskID == null) return;

    return updateFrameWorkState({
      frameSeq,
      taskID,
      workState: prevWorkState,
    });
  };

  return !isRevertButtonVisible ? null : (
    <VIconButton
      Icon={RestartAlt}
      size="xs"
      variant="roundPrimary"
      onClick={handleClick}
    />
  );
};

TaskLayoutHeaderWorkStateRevertButton.displayName =
  'TaskLayoutHeaderWorkStateRevertButton';
